import { useState } from 'react';
import type {
  CreateFormData,
  CreateStepData,
  CreateFieldData,
  CreateOptionData,
  FormFieldType,
} from '@wordpuppi/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

const generateSlug = (value: string) => {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
};

const FIELD_TYPES: { value: FormFieldType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'email', label: 'Email' },
  { value: 'url', label: 'URL' },
  { value: 'tel', label: 'Phone' },
  { value: 'number', label: 'Number' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'select', label: 'Dropdown' },
  { value: 'radio', label: 'Radio Buttons' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'date', label: 'Date' },
  { value: 'hidden', label: 'Hidden' },
];

interface FormBuilderProps {
  initialData?: CreateFormData;
  onSubmit: (data: CreateFormData) => void;
  isSubmitting?: boolean;
  submitLabel?: string;
  /**
   * True when editing an already-created form (locks field keys, warns on type
   * change). New forms seeded from a template still pass initialData but leave
   * this false so the fields stay freely editable.
   */
  isEditing?: boolean;
}

export function FormBuilder({
  initialData,
  onSubmit,
  isSubmitting,
  submitLabel = 'Save Form',
  isEditing = false,
}: FormBuilderProps) {
  const [name, setName] = useState(initialData?.name ?? '');
  const [slug, setSlug] = useState(initialData?.slug ?? '');
  const [description, setDescription] = useState(initialData?.description ?? '');
  const [submitButtonLabel, setSubmitButtonLabel] = useState(
    initialData?.settings?.submit_label ?? 'Submit'
  );
  const [successMessage, setSuccessMessage] = useState(
    initialData?.settings?.success_message ?? 'Thank you for your submission!'
  );
  const [actionUrl, setActionUrl] = useState(initialData?.settings?.action_url ?? '');

  // Notification settings
  const [notifyEmails, setNotifyEmails] = useState(
    initialData?.settings?.notify_emails?.join(', ') ?? ''
  );
  const [sendConfirmation, setSendConfirmation] = useState(
    initialData?.settings?.send_confirmation ?? false
  );
  const [webhookUrl, setWebhookUrl] = useState(
    initialData?.settings?.webhook_url ?? ''
  );

  // Spam protection
  const [enableTurnstile, setEnableTurnstile] = useState(
    initialData?.settings?.enable_turnstile ?? false
  );

  // Members bridge (part-14): add valid submitters to the site's Members list.
  const [addToMembers, setAddToMembers] = useState(
    initialData?.settings?.add_to_members ?? false
  );

  // Action URL is plumbing — tuck it behind an Advanced disclosure.
  const [advancedOpen, setAdvancedOpen] = useState(
    !!initialData?.settings?.action_url
  );

  // Determine action URL mode from initial value
  const getActionUrlMode = (url: string) => {
    if (!url) return 'default';
    if (url.startsWith('api/forms/') || url.startsWith('/api/forms/')) return 'api';
    if (url.startsWith('forms/') && url.endsWith('/submit')) return 'default';
    return 'custom';
  };
  const [actionUrlMode, setActionUrlMode] = useState(getActionUrlMode(initialData?.settings?.action_url ?? ''));

  const [steps, setSteps] = useState<CreateStepData[]>(
    initialData?.steps ?? [{ name: 'Main', fields: [] }]
  );

  const [expandedStep, setExpandedStep] = useState<number>(0);
  const [expandedField, setExpandedField] = useState<{ step: number; field: number } | null>(null);

  const handleNameChange = (value: string) => {
    setName(value);
    if (!isEditing) {
      setSlug(generateSlug(value));
    }
  };

  const addStep = () => {
    setSteps([...steps, { name: `Step ${steps.length + 1}`, fields: [] }]);
    setExpandedStep(steps.length);
  };

  const removeStep = (index: number) => {
    if (steps.length === 1) return;
    setSteps(steps.filter((_, i) => i !== index));
    if (expandedStep >= index && expandedStep > 0) {
      setExpandedStep(expandedStep - 1);
    }
  };

  const updateStep = (index: number, updates: Partial<CreateStepData>) => {
    setSteps(steps.map((s, i) => (i === index ? { ...s, ...updates } : s)));
  };

  const addField = (stepIndex: number) => {
    const newField: CreateFieldData = {
      name: '',
      label: 'New Field',
      field_type: 'text',
    };
    const updatedSteps = [...steps];
    updatedSteps[stepIndex].fields.push(newField);
    setSteps(updatedSteps);
    setExpandedField({ step: stepIndex, field: updatedSteps[stepIndex].fields.length - 1 });
  };

  const removeField = (stepIndex: number, fieldIndex: number) => {
    const updatedSteps = [...steps];
    updatedSteps[stepIndex].fields = updatedSteps[stepIndex].fields.filter(
      (_, i) => i !== fieldIndex
    );
    setSteps(updatedSteps);
    setExpandedField(null);
  };

  const updateField = (
    stepIndex: number,
    fieldIndex: number,
    updates: Partial<CreateFieldData>
  ) => {
    const updatedSteps = [...steps];
    updatedSteps[stepIndex].fields[fieldIndex] = {
      ...updatedSteps[stepIndex].fields[fieldIndex],
      ...updates,
    };
    setSteps(updatedSteps);
  };

  const moveField = (stepIndex: number, fieldIndex: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? fieldIndex - 1 : fieldIndex + 1;
    if (newIndex < 0 || newIndex >= steps[stepIndex].fields.length) return;

    const updatedSteps = [...steps];
    const fields = [...updatedSteps[stepIndex].fields];
    [fields[fieldIndex], fields[newIndex]] = [fields[newIndex], fields[fieldIndex]];
    updatedSteps[stepIndex].fields = fields;
    setSteps(updatedSteps);

    if (expandedField?.step === stepIndex && expandedField.field === fieldIndex) {
      setExpandedField({ step: stepIndex, field: newIndex });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const stepsWithOrder = steps.map((step, stepIdx) => ({
      ...step,
      order: stepIdx,
      fields: step.fields.map((field, fieldIdx) => ({
        ...field,
        order: fieldIdx,
        name: field.name || generateSlug(field.label),
      })),
    }));

    // Parse notify emails from comma-separated string
    const parsedNotifyEmails = notifyEmails
      .split(',')
      .map((e) => e.trim())
      .filter((e) => e.length > 0);

    const data: CreateFormData = {
      name,
      slug,
      description: description || undefined,
      settings: {
        submit_label: submitButtonLabel || undefined,
        success_message: successMessage || undefined,
        action_url: actionUrlMode === 'api'
          ? `api/forms/${slug}`
          : actionUrlMode === 'default'
            ? undefined  // Let anyform default to forms/{slug}
            : (actionUrl || undefined),
        notify_emails: parsedNotifyEmails.length > 0 ? parsedNotifyEmails : undefined,
        send_confirmation: sendConfirmation || undefined,
        webhook_url: webhookUrl || undefined,
        enable_turnstile: enableTurnstile || undefined,
        add_to_members: addToMembers || undefined,
      },
      steps: stepsWithOrder,
    };

    onSubmit(data);
  };

  // Check if form has an email field (for confirmation option)
  const hasEmailField = steps.some((step) =>
    step.fields.some((field) => field.field_type === 'email')
  );

  // Field list + "Add Field" for one step. Shared by the single-step flat card
  // and the multi-step accordion so both render fields identically.
  const renderFieldList = (stepIndex: number) => {
    const step = steps[stepIndex];
    return (
      <div className="space-y-2">
        {step.fields.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            No fields yet. Add your first field below.
          </p>
        ) : (
          <div className="space-y-2">
            {step.fields.map((field, fieldIndex) => (
              <FieldEditor
                key={fieldIndex}
                field={field}
                fieldIndex={fieldIndex}
                totalFields={step.fields.length}
                isExistingForm={isEditing}
                isExpanded={
                  expandedField?.step === stepIndex &&
                  expandedField?.field === fieldIndex
                }
                onExpand={() =>
                  setExpandedField(
                    expandedField?.step === stepIndex &&
                      expandedField?.field === fieldIndex
                      ? null
                      : { step: stepIndex, field: fieldIndex }
                  )
                }
                onUpdate={(updates) => updateField(stepIndex, fieldIndex, updates)}
                onRemove={() => removeField(stepIndex, fieldIndex)}
                onMove={(dir) => moveField(stepIndex, fieldIndex, dir)}
              />
            ))}
          </div>
        )}
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => addField(stepIndex)}
        >
          + Add Field
        </Button>
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Form Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Form Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Form Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Contact Form"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="slug">Slug</Label>
              <Input
                id="slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="contact-form"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description (optional)</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="A brief description of this form"
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="submitLabel">Submit Button Text</Label>
              <Input
                id="submitLabel"
                value={submitButtonLabel}
                onChange={(e) => setSubmitButtonLabel(e.target.value)}
                placeholder="Submit"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="successMessage">Success Message</Label>
              <Input
                id="successMessage"
                value={successMessage}
                onChange={(e) => setSuccessMessage(e.target.value)}
                placeholder="Thank you for your submission!"
              />
            </div>
          </div>

          {/* Advanced: action URL is internal routing plumbing — hidden by default. */}
          <div className="space-y-3 pt-2">
            <button
              type="button"
              onClick={() => setAdvancedOpen(!advancedOpen)}
              className="flex items-center gap-2 text-sm font-medium hover:text-foreground/80"
            >
              <ChevronDown className={`h-4 w-4 transition-transform ease-out ${advancedOpen ? 'rotate-180' : ''}`} />
              Advanced
            </button>
            {advancedOpen && (
              <div className="disclosure-reveal space-y-2 pl-6 border-l-2 border-muted">
                <Label>Action URL</Label>
                <div className="flex gap-2">
                  <Select
                    value={actionUrlMode}
                    onValueChange={(mode: string) => {
                      setActionUrlMode(mode);
                      if (mode === 'default') {
                        setActionUrl('');
                      } else if (mode === 'api') {
                        setActionUrl(`api/forms/${slug}`);
                      }
                    }}
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Default</SelectItem>
                      <SelectItem value="api">Anyform API</SelectItem>
                      <SelectItem value="custom">Custom URL</SelectItem>
                    </SelectContent>
                  </Select>
                  {actionUrlMode === 'custom' && (
                    <Input
                      className="flex-1"
                      value={actionUrl}
                      onChange={(e) => setActionUrl(e.target.value)}
                      placeholder="https://example.com/webhook"
                    />
                  )}
                  {actionUrlMode === 'api' && (
                    <Input
                      className="flex-1 bg-muted"
                      value={`api/forms/${slug || '{slug}'}`}
                      disabled
                    />
                  )}
                  {actionUrlMode === 'default' && (
                    <Input
                      className="flex-1 bg-muted"
                      value={`forms/${slug || '{slug}'}/submit`}
                      disabled
                    />
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {actionUrlMode === 'default' && 'Default: submissions post to the built-in endpoint (works with notifications). Leave this unless you know you need a custom target.'}
                  {actionUrlMode === 'api' && 'Form submits to Anyform API endpoint.'}
                  {actionUrlMode === 'custom' && 'Form submits to your custom endpoint.'}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Notifications */}
      <Card>
        <CardHeader>
          <CardTitle>Notifications</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3 pb-2 border-b">
            <input
              type="checkbox"
              id="addToMembers"
              checked={addToMembers}
              onChange={(e) => setAddToMembers(e.target.checked)}
              className="h-4 w-4 rounded border-input accent-primary"
            />
            <div>
              <Label htmlFor="addToMembers">Add submitters to Members</Label>
              <p className="text-xs text-muted-foreground">
                Turns this form into a subscriber form — valid submissions are added to your
                Members list. Best paired with an email field.
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notifyEmails">Notify on submission</Label>
            <Input
              id="notifyEmails"
              value={notifyEmails}
              onChange={(e) => setNotifyEmails(e.target.value)}
              placeholder="admin@example.com, team@example.com"
            />
            <p className="text-xs text-muted-foreground">
              Comma-separated email addresses to notify when form is submitted
            </p>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="sendConfirmation"
              checked={sendConfirmation}
              onChange={(e) => setSendConfirmation(e.target.checked)}
              disabled={!hasEmailField}
              className="h-4 w-4 rounded border-input accent-primary"
            />
            <div>
              <Label
                htmlFor="sendConfirmation"
                className={cn(!hasEmailField && 'text-muted-foreground')}
              >
                Send confirmation to submitter
              </Label>
              {!hasEmailField && (
                <p className="text-xs text-muted-foreground">
                  Add an email field to enable this option
                </p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="webhookUrl">Webhook URL (optional)</Label>
            <Input
              id="webhookUrl"
              type="url"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              placeholder="https://hooks.example.com/form-submit"
            />
            <p className="text-xs text-muted-foreground">
              POST submission data to this URL when form is submitted
            </p>
          </div>

          <div className="flex items-center gap-3 pt-2 border-t">
            <input
              type="checkbox"
              id="enableTurnstile"
              checked={enableTurnstile}
              onChange={(e) => setEnableTurnstile(e.target.checked)}
              className="h-4 w-4 rounded border-input accent-primary"
            />
            <div>
              <Label htmlFor="enableTurnstile">
                Enable Cloudflare Turnstile captcha
              </Label>
              <p className="text-xs text-muted-foreground">
                Adds a Turnstile challenge to prevent spam submissions — managed by
                WordPuppi, no Cloudflare account needed
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Fields — single-step forms show a flat field list; the multi-step
          accordion only appears once a second step is added (most forms need one). */}
      {steps.length === 1 ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Fields</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {renderFieldList(0)}
            <div className="pt-3 border-t">
              <Button type="button" variant="ghost" size="sm" onClick={addStep}>
                + Add step
              </Button>
              <p className="text-xs text-muted-foreground mt-1">
                Split a long form into multiple steps. Most forms need just one.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium">Steps &amp; Fields</h3>
            <span className="text-sm text-muted-foreground">
              {steps.length} steps
            </span>
          </div>

          {steps.map((step, stepIndex) => (
            <Card key={stepIndex} className={cn(expandedStep === stepIndex && 'ring-2 ring-primary')}>
              <CardHeader
                className="cursor-pointer"
                onClick={() => setExpandedStep(expandedStep === stepIndex ? -1 : stepIndex)}
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground font-mono text-sm">
                      {stepIndex + 1}.
                    </span>
                    <CardTitle className="text-base">{step.name}</CardTitle>
                    <span className="text-sm text-muted-foreground">
                      ({step.fields.length} {step.fields.length === 1 ? 'field' : 'fields'})
                    </span>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeStep(stepIndex);
                    }}
                  >
                    Remove Step
                  </Button>
                </div>
              </CardHeader>

              {expandedStep === stepIndex && (
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Step Name</Label>
                    <Input
                      value={step.name}
                      onChange={(e) => updateStep(stepIndex, { name: e.target.value })}
                      placeholder="Step name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Step Description (optional)</Label>
                    <Input
                      value={step.description ?? ''}
                      onChange={(e) => updateStep(stepIndex, { description: e.target.value })}
                      placeholder="Instructions for this step"
                    />
                  </div>

                  {/* Fields */}
                  <div className="space-y-2 pt-4 border-t">
                    <Label>Fields</Label>
                    {renderFieldList(stepIndex)}
                  </div>
                </CardContent>
              )}
            </Card>
          ))}

          <Button type="button" variant="outline" onClick={addStep}>
            + Add Step
          </Button>
        </div>
      )}

      {/* Submit */}
      <div className="flex gap-2">
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : submitLabel}
        </Button>
      </div>
    </form>
  );
}

interface FieldEditorProps {
  field: CreateFieldData;
  fieldIndex: number;
  totalFields: number;
  isExistingForm: boolean;
  isExpanded: boolean;
  onExpand: () => void;
  onUpdate: (updates: Partial<CreateFieldData>) => void;
  onRemove: () => void;
  onMove: (direction: 'up' | 'down') => void;
}

function FieldEditor({
  field,
  fieldIndex,
  totalFields,
  isExistingForm,
  isExpanded,
  onExpand,
  onUpdate,
  onRemove,
  onMove,
}: FieldEditorProps) {
  const [pendingTypeChange, setPendingTypeChange] = useState<FormFieldType | null>(null);
  const needsOptions = ['select', 'radio', 'multi_select'].includes(field.field_type);

  const handleTypeChange = (newType: FormFieldType) => {
    if (isExistingForm && newType !== field.field_type) {
      setPendingTypeChange(newType);
    } else {
      onUpdate({ field_type: newType });
    }
  };

  const confirmTypeChange = () => {
    if (pendingTypeChange) {
      onUpdate({ field_type: pendingTypeChange });
      setPendingTypeChange(null);
    }
  };

  const addOption = () => {
    const options = field.options ?? [];
    onUpdate({
      options: [...options, { label: `Option ${options.length + 1}`, value: `option_${options.length + 1}` }],
    });
  };

  const updateOption = (index: number, updates: Partial<CreateOptionData>) => {
    const options = [...(field.options ?? [])];
    options[index] = { ...options[index], ...updates };
    onUpdate({ options });
  };

  const removeOption = (index: number) => {
    onUpdate({ options: field.options?.filter((_, i) => i !== index) });
  };

  return (
    <div className={cn('border rounded-lg', isExpanded && 'ring-1 ring-primary')}>
      <div
        className="p-3 flex justify-between items-center cursor-pointer hover:bg-muted/50"
        onClick={onExpand}
      >
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground text-sm">{fieldIndex + 1}.</span>
          <span className="font-medium">{field.label}</span>
          <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
            {field.field_type}
          </span>
          {field.required && (
            <span className="text-xs text-destructive">required</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onMove('up');
            }}
            disabled={fieldIndex === 0}
          >
            ↑
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onMove('down');
            }}
            disabled={fieldIndex === totalFields - 1}
          >
            ↓
          </Button>
        </div>
      </div>

      {isExpanded && (
        <div className="p-4 pt-0 border-t space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Label</Label>
              <Input
                value={field.label}
                onChange={(e) => onUpdate({ label: e.target.value })}
                placeholder="Field label"
              />
            </div>
            <div className="space-y-2">
              <Label>Field Key</Label>
              <Input
                value={field.name}
                onChange={(e) => onUpdate({ name: e.target.value })}
                placeholder={field.label ? generateSlug(field.label) : 'your-field-name'}
                disabled={isExistingForm}
                className={isExistingForm ? 'bg-muted' : ''}
              />
              {isExistingForm ? (
                <p className="text-xs text-muted-foreground">
                  Field key cannot be changed after form is created
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Used as the HTML input name. Auto-generated from label if empty.
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select
                value={field.field_type}
                onValueChange={(v: string) => handleTypeChange(v as FormFieldType)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Required</Label>
              <Select
                value={field.required ? 'yes' : 'no'}
                onValueChange={(v: string) => onUpdate({ required: v === 'yes' })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no">No</SelectItem>
                  <SelectItem value="yes">Yes</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Placeholder (optional)</Label>
            <Input
              value={field.placeholder ?? ''}
              onChange={(e) => onUpdate({ placeholder: e.target.value })}
              placeholder="Enter placeholder text"
            />
          </div>

          <div className="space-y-2">
            <Label>Help Text (optional)</Label>
            <Input
              value={field.help_text ?? ''}
              onChange={(e) => onUpdate({ help_text: e.target.value })}
              placeholder="Additional help text shown below the field"
            />
          </div>

          {/* Options for select/radio */}
          {needsOptions && (
            <div className="space-y-2 pt-2 border-t">
              <Label>Options</Label>
              {(field.options ?? []).map((opt, optIdx) => (
                <div key={optIdx} className="flex gap-2 items-center">
                  <Input
                    value={opt.label}
                    onChange={(e) => updateOption(optIdx, { label: e.target.value })}
                    placeholder="Label"
                    className="flex-1"
                  />
                  <Input
                    value={opt.value}
                    onChange={(e) => updateOption(optIdx, { value: e.target.value })}
                    placeholder="Value"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeOption(optIdx)}
                  >
                    ×
                  </Button>
                </div>
              ))}
              <Button type="button" variant="secondary" size="sm" onClick={addOption}>
                + Add Option
              </Button>
            </div>
          )}

          {/* Validation Rules */}
          {['text', 'textarea', 'number'].includes(field.field_type) && (
            <div className="space-y-2 pt-2 border-t">
              <Label>Validation (optional)</Label>
              <div className="grid grid-cols-2 gap-4">
                {['text', 'textarea'].includes(field.field_type) && (
                  <>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Min Length</Label>
                      <Input
                        type="number"
                        value={field.validation_rules?.min_length ?? ''}
                        onChange={(e) =>
                          onUpdate({
                            validation_rules: {
                              ...field.validation_rules,
                              min_length: e.target.value ? parseInt(e.target.value) : undefined,
                            },
                          })
                        }
                        placeholder="0"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Max Length</Label>
                      <Input
                        type="number"
                        value={field.validation_rules?.max_length ?? ''}
                        onChange={(e) =>
                          onUpdate({
                            validation_rules: {
                              ...field.validation_rules,
                              max_length: e.target.value ? parseInt(e.target.value) : undefined,
                            },
                          })
                        }
                        placeholder="Unlimited"
                      />
                    </div>
                  </>
                )}
                {field.field_type === 'number' && (
                  <>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Min Value</Label>
                      <Input
                        type="number"
                        value={field.validation_rules?.min ?? ''}
                        onChange={(e) =>
                          onUpdate({
                            validation_rules: {
                              ...field.validation_rules,
                              min: e.target.value ? parseFloat(e.target.value) : undefined,
                            },
                          })
                        }
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Max Value</Label>
                      <Input
                        type="number"
                        value={field.validation_rules?.max ?? ''}
                        onChange={(e) =>
                          onUpdate({
                            validation_rules: {
                              ...field.validation_rules,
                              max: e.target.value ? parseFloat(e.target.value) : undefined,
                            },
                          })
                        }
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          <div className="pt-2">
            <Button type="button" variant="secondary" size="sm" onClick={onRemove}>
              Remove Field
            </Button>
          </div>
        </div>
      )}

      {/* Type Change Confirmation Dialog */}
      <Dialog open={!!pendingTypeChange} onOpenChange={() => setPendingTypeChange(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Field Type?</DialogTitle>
            <DialogDescription>
              Changing the field type will make existing submission data for this field
              unreadable. The data will not be deleted, but it may not display correctly
              with the new field type.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setPendingTypeChange(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmTypeChange}>
              Change Type
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
